import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import useMountEffect from '../../../hooks/useMountEffect';
import { DEFAULT_LARGE_TRANSITION_DURATION_MS } from '../../../constants/statics';
const WAIT_FOR_THINGS_TO_SETTLE = 500;
const VerticalExpander = ({ children, maxHeight, transitionDurationMs = DEFAULT_LARGE_TRANSITION_DURATION_MS, }) => {
    const [height, _setHeight] = useState(0);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const heightRef = useRef(0);
    const contentRef = useRef(null);
    const observerRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const setHeight = useCallback((newHeight) => {
        heightRef.current = newHeight;
        _setHeight(newHeight);
    }, []);
    const getConstrainedHeight = useCallback(() => {
        return maxHeight && heightRef.current > maxHeight
            ? maxHeight
            : heightRef.current;
    }, [maxHeight]);
    const handleTransitionEnd = () => {
        requestAnimationFrame(() => {
            if (contentRef.current) {
                contentRef.current.style.height = `${getConstrainedHeight()}px`;
                setTimeout(() => {
                    if (contentRef.current) {
                        contentRef.current.style.height = '';
                    }
                    setInitialLoadComplete(true);
                }, WAIT_FOR_THINGS_TO_SETTLE);
            }
        });
    };
    useMountEffect(() => {
        setTimeout(() => {
            handleTransitionEnd();
        }, transitionDurationMs + WAIT_FOR_THINGS_TO_SETTLE);
    });
    useEffect(() => {
        if (contentRef.current) {
            const initialHeight = contentRef.current.scrollHeight;
            setHeight(initialHeight);
            observerRef.current = new MutationObserver(() => {
                if (contentRef.current) {
                    const targetHeight = contentRef.current.scrollHeight;
                    if (heightRef.current !== targetHeight) {
                        setHeight(targetHeight);
                    }
                }
            });
            observerRef.current.observe(contentRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
            });
            resizeObserverRef.current = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const targetHeight = entry.target.scrollHeight;
                    if (heightRef.current !== targetHeight) {
                        setHeight(targetHeight);
                    }
                }
            });
            resizeObserverRef.current.observe(contentRef.current);
        }
        return () => {
            observerRef?.current?.disconnect();
            resizeObserverRef?.current?.disconnect();
        };
    }, [children, setHeight]);
    const transition = `height ${initialLoadComplete ? transitionDurationMs : transitionDurationMs / 2}ms cubic-bezier(0.5, -.1, 0.5, 1.2)`;
    return (_jsx("div", { style: {
            overflow: maxHeight && height > maxHeight ? 'auto' : 'hidden',
            height: `${getConstrainedHeight()}px`,
            transition,
            maxHeight: maxHeight ? `${maxHeight}px` : 'none',
        }, children: _jsx("div", { ref: contentRef, children: children }) }));
};
export default VerticalExpander;
//# sourceMappingURL=index.js.map